import { Injectable, inject } from '@angular/core';
import {
	Auth,
	signInWithCustomToken,
	user,
	User,
	authState,
	idToken,
} from '@angular/fire/auth';
import { DateTime, Duration } from 'luxon';
import { Observable } from 'rxjs';
import { distinct, map, switchMap } from 'rxjs/operators';
import { AssignedRole } from 'src/app/models/data/assignedRole';
import { ApiService, PasswordResetTokenStatus } from './api.services';
import { COOKIE_CONSENT_STORAGE_KEY } from './cookie-consent.service';
import { NavStateService } from '../nav/nav-state.service';
import SystemCapability = Components.Schemas.SystemCapability;
import UserAuthentication = Components.Schemas.UserAuthentication;
import TokenValidity = Components.Schemas.TokenValidity;

export type UserSession = {
	token?: string;
	claims?: { [key: string]: any };
	systemCapabilities?: SystemCapability[];
	assignedRole?: AssignedRole;
};
export const isLoggedIn = (userSession: UserSession): boolean =>
	!!userSession.token;

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private logoutReason?: string;
	private autoLogoutTimer?: any;
	private auth: Auth = inject(Auth);
	user$ = user(this.auth);
	authState$ = authState(this.auth);
	idToken$ = idToken(this.auth);

	constructor(
		private apiService: ApiService,
		private navStateService: NavStateService
	) {}

	userSession(): Observable<UserSession> {
		return this.user$.pipe(
			distinct(),
			switchMap(async (myUser: User | null) => await toUserSession(myUser))
		);
	}

	async login(
		email: string,
		password: string,
		allDaySession: boolean
	): Promise<void> {
		let sessionKeepActiveTimeSeconds;
		if (allDaySession) {
			sessionKeepActiveTimeSeconds = Math.floor(
				DateTime.now().endOf('day').diffNow('seconds').seconds
			);
		}

		const userAuthentication = await this.apiService.login(
			email,
			password,
			sessionKeepActiveTimeSeconds
		);
		await this.loginToFirebase(userAuthentication);
	}

	async verifyMfaToken(token: string): Promise<UserAuthentication> {
		const userAuthentication = await this.apiService.verifyMfaToken(token);
		if (!userAuthentication.mfaRequired) {
			await this.loginToFirebase(userAuthentication);
		}
		return userAuthentication;
	}

	async updateCustomClaims(activeEntityRelationshipId: string): Promise<void> {
		const userAuthentication = await this.apiService.updateCustomClaims(
			activeEntityRelationshipId
		);
		await this.loginToFirebase(userAuthentication);
	}

	async logout(logoutReason?: string): Promise<void> {
		this.logoutReason = logoutReason;
		this.navStateService.hasShownMobileSidenav = false;

		// Cookie consent is browser-scoped; keep it across logout / session wipe.
		const cookieConsent = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
		localStorage.clear();
		if (cookieConsent === 'accepted' || cookieConsent === 'rejected') {
			localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, cookieConsent);
		}
		this.clearLogoutTimer();
		await this.auth.signOut();
	}

	async getPasswordResetToken(email: string): Promise<void> {
		return this.apiService.sendPasswordResetEmail(email);
	}

	async checkPasswordResetTokenStatus(
		token: string
	): Promise<PasswordResetTokenStatus> {
		return this.apiService.checkPasswordResetTokenStatus(token);
	}

	async resetPassword(password: string, token: string): Promise<void> {
		await this.apiService.resetPassword(password, token);
	}

	async checkUserRegistrationTokenStatus(
		token: string
	): Promise<TokenValidity> {
		return await this.apiService.checkNewUserRegistrationToken(token);
	}

	async completeNewUserRegistration(
		password: string,
		token: string,
		userId: string,
		firstName: string,
		lastName: string,
		securityQuestion: string,
		securityAnswer: string
	): Promise<void> {
		return await this.apiService.completeNewUserRegistration(
			password,
			token,
			userId,
			firstName,
			lastName,
			securityQuestion,
			securityAnswer
		);
	}

	resetLogoutTimer(userSession: UserSession | null): void {
		this.clearLogoutTimer();
		if (userSession?.claims) {
			const sessionExpirationMs = userSession.claims.sessionExpiration * 1000;
			const allowedInactiveMs = userSession.claims.sessionInactive * 1000;

			const sessionExpiration = DateTime.fromMillis(sessionExpirationMs);
			const inactiveExpiration = DateTime.now().plus(
				Duration.fromMillis(allowedInactiveMs)
			);
			const expirationMs = DateTime.max(sessionExpiration, inactiveExpiration);

			const millisDifference =
				expirationMs.diffNow('milliseconds').milliseconds;
			this.setLogoutTimer(millisDifference);
		}
	}

	private async loginToFirebase(
		userAuthentication: UserAuthentication
	): Promise<void> {
		this.logoutReason = '';

		const { systemCapabilities, assignedRole } = userAuthentication;
		localStorage.setItem(
			userSessionData,
			JSON.stringify({ systemCapabilities, assignedRole })
		);

		await signInWithCustomToken(this.auth, userAuthentication.loginToken);
	}

	private setLogoutTimer(delayMs: number): void {
		this.clearLogoutTimer();
		this.autoLogoutTimer = setTimeout(() => {
			this.logout('You have been logged out due to inactivity.').then(() => {});
		}, delayMs);
	}

	private clearLogoutTimer(): void {
		if (this.autoLogoutTimer) {
			clearTimeout(this.autoLogoutTimer);
			this.autoLogoutTimer = undefined;
		}
	}
}

const userSessionData = 'userSessionData';
const loggedOut: UserSession = {};

const toUserSession = async (myUser: User | null): Promise<UserSession> => {
	if (!myUser) {
		return loggedOut;
	}

	let tokenResult = await myUser.getIdTokenResult();

	const { token, claims } = tokenResult;
	const data = localStorage.getItem(userSessionData) ?? '{}';
	const { systemCapabilities, assignedRole } = JSON.parse(data);
	return { token, claims, systemCapabilities, assignedRole };
};
